import { useState, type FormEvent } from 'react';
import { Mail, Cloud, Shield, FileSpreadsheet, CheckCircle2, AlertCircle, Send, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { useIntegrationStatus, useSendTestEmail } from '@/hooks/useSettings';

export function IntegrationsSettings() {
  const { data, isLoading } = useIntegrationStatus();
  if (isLoading) return <LoaderScreen />;

  return (
    <div className="space-y-6">
      <div>
        <div className="kicker mb-2">Integrations</div>
        <h2 className="font-display text-2xl text-text mb-2">Connected Services</h2>
        <p className="text-sm text-text-muted max-w-2xl">
          External services this portal depends on. Credentials are stored in Firebase Functions Secret Manager
          and never exposed to the browser.
        </p>
      </div>

      <SesCard
        configured={data?.ses.configured ?? false}
        from={data?.ses.from}
        sandbox={data?.ses.sandbox}
      />

      <S3Card configured={data?.s3.configured ?? false} bucket={data?.s3.bucket} />

      <AppCheckCard enabled={data?.appCheck.enabled ?? false} />

      <QuickbooksCard connected={data?.quickbooks?.connected ?? false} />
    </div>
  );
}

function IntegrationShell({
  icon: Icon, name, vendor, status, statusTone, children,
}: {
  icon: typeof Mail;
  name: string;
  vendor: string;
  status: string;
  statusTone: 'success' | 'warning' | 'danger' | 'neutral';
  children?: React.ReactNode;
}) {
  return (
    <Card>
      <div className="flex items-start gap-4 mb-5 pb-5 border-b border-border">
        <div className="shrink-0 h-11 w-11 rounded-sm bg-bg-subtle border border-border-strong flex items-center justify-center">
          <Icon size={18} className="text-brass-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="font-display text-lg text-text">{name}</h3>
            <Badge tone={statusTone}>{status}</Badge>
          </div>
          <div className="kicker">{vendor}</div>
        </div>
      </div>
      {children}
    </Card>
  );
}

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <div className="kicker mb-1">{label}</div>
      <div className={`text-sm text-text ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

function SesCard({ configured, from, sandbox }: { configured: boolean; from?: string; sandbox?: boolean }) {
  const test = useSendTestEmail();
  const [testTo, setTestTo] = useState('');

  async function onSendTest(e: FormEvent) {
    e.preventDefault();
    try {
      const result = await test.mutateAsync(testTo.trim());
      toast.success('Test email sent', { description: `MessageID: ${result.messageId.slice(0, 16)}…` });
      setTestTo('');
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Failed to send test email.');
    }
  }

  return (
    <IntegrationShell
      icon={Mail}
      name="AWS SES"
      vendor="Email Delivery · Amazon"
      status={configured ? (sandbox ? 'Sandbox' : 'Connected') : 'Not configured'}
      statusTone={configured ? (sandbox ? 'warning' : 'success') : 'danger'}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Field label="From Address" value={from ?? <span className="text-text-dim">—</span>} mono />
        <Field
          label="Mode"
          value={
            <span className="inline-flex items-center gap-2">
              {sandbox ? <AlertCircle size={13} className="text-warning" /> : <CheckCircle2 size={13} className="text-success" />}
              {sandbox ? 'Sandbox (verified recipients only)' : 'Production'}
            </span>
          }
        />
      </div>

      <div className="pt-5 border-t border-border">
        <h4 className="text-sm text-text mb-2">Send a test email</h4>
        <p className="text-xs text-text-muted mb-4 max-w-md">
          Confirms SES credentials and DKIM are working. The recipient must be a verified address while in sandbox mode.
        </p>
        <form onSubmit={onSendTest} className="flex gap-3 max-w-md">
          <Input
            placeholder="recipient@example.com"
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            required
          />
          <Button
            type="submit"
            variant="secondary"
            leftIcon={<Send size={13} />}
            loading={test.isPending}
            disabled={!configured}
          >
            Send Test
          </Button>
        </form>
      </div>
    </IntegrationShell>
  );
}

function S3Card({ configured, bucket }: { configured: boolean; bucket?: string }) {
  return (
    <IntegrationShell
      icon={Cloud}
      name="AWS S3 + CloudFront"
      vendor="Asset Storage · Amazon"
      status={configured ? 'Connected' : 'Not configured'}
      statusTone={configured ? 'success' : 'danger'}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="DAM Bucket" value={bucket ?? <span className="text-text-dim">—</span>} mono />
        <Field
          label="Delivery"
          value="CloudFront CDN with Origin Access Control"
        />
      </div>
      <div className="mt-6 pt-5 border-t border-border">
        <p className="text-xs text-text-muted leading-relaxed">
          High-resolution veneer photos, installation galleries, spec sheets and signed-document PDFs are stored here.
          Direct uploads from the browser via presigned URLs.
        </p>
      </div>
    </IntegrationShell>
  );
}

function AppCheckCard({ enabled }: { enabled: boolean }) {
  return (
    <IntegrationShell
      icon={Shield}
      name="Firebase App Check"
      vendor="Abuse Protection · Google"
      status={enabled ? 'Enabled' : 'Disabled'}
      statusTone={enabled ? 'success' : 'warning'}
    >
      <p className="text-sm text-text-muted leading-relaxed">
        Validates that requests come from this authorized web app via reCAPTCHA v3, blocking automated abuse of
        Auth, Firestore, Storage and Functions endpoints. Highly recommended for production.
      </p>
      {!enabled && (
        <div className="mt-5 p-3 border border-warning/30 bg-warning/5 rounded-sm">
          <p className="text-xs text-warning leading-relaxed">
            App Check is not enabled. Configure a reCAPTCHA v3 site key and set <span className="font-mono">VITE_RECAPTCHA_SITE_KEY</span> in your frontend env, then redeploy.
          </p>
        </div>
      )}
    </IntegrationShell>
  );
}

function QuickbooksCard({ connected }: { connected: boolean }) {
  return (
    <IntegrationShell
      icon={FileSpreadsheet}
      name="QuickBooks Online"
      vendor="Accounting Sync · Intuit"
      status={connected ? 'Connected' : 'Not connected'}
      statusTone={connected ? 'success' : 'neutral'}
    >
      <p className="text-sm text-text-muted leading-relaxed mb-5">
        Optional. When connected, invoices and bills created here will sync to your QuickBooks Online account
        for the year-end CPA handoff.
      </p>
      <Button variant="secondary" rightIcon={<ExternalLink size={13} />} disabled>
        {connected ? 'Manage Connection' : 'Connect QuickBooks'}
      </Button>
      <p className="text-xs text-text-dim mt-3">Available in Phase 2.</p>
    </IntegrationShell>
  );
}
